<template>
  <IonApp>
    <IonPage id="main-content">
      <IonHeader class="py-4">
        <IonToolbar>
          <IonTitle>
            <router-link to="/" class="inline-block max-w-[200px]">
              <img src="/img/logo-main.svg" alt="GEOcard" class="h-9" />
            </router-link>
          </IonTitle>

          <!-- eslint-disable-next-line vue/no-deprecated-slot-attribute -->
          <IonButtons slot="end">
            <IonButton @click="openAccountMenu">
              <div>
                <IonIcon :icon="personSharp" color="primary" style="font-size: 24px" />
              </div>
            </IonButton>

            <IonButton v-if="isLoggedIn && currentUser?.subs.length > 0" id="notification-button" @click="openPopover">
              <div>
                <IonIcon :icon="notificationsSharp" color="primary" style="font-size: 24px" />
                <IonBadge v-if="unreadCount > 0" color="danger" style="font-size: x-small">
                  {{ unreadCount }}
                </IonBadge>
              </div>
            </IonButton>
            <IonPopover
              trigger="notification-button"
              :is-open="popoverOpen"
              @didDismiss="closePopover"
              side="bottom"
              alignment="start"
              size="auto"
              v-if="isLoggedIn && currentUser?.subs.length > 0"
            >
              <div class="p-2 max-h-60">
                <IonList>
                  <IonListHeader lines="inset">
                    <h3>Benachrichtigungen</h3>
                  </IonListHeader>
                  <IonItem
                    v-for="msg in messages"
                    :key="msg.id"
                    :button="true"
                    :detail="false"
                    @click="goToMessage(msg.id)"
                  >
                    <IonLabel class="flex justify-between items-center w-full">
                      <span
                        v-if="unreadMessages.find((m) => m.id === msg.id)"
                        class="text-primary text-xs whitespace-nowrap me-2 align-middle"
                      >
                        ●
                      </span>
                      <span class="text-sm">{{ msg.subject }}</span>
                    </IonLabel>
                  </IonItem>
                </IonList>
              </div>
            </IonPopover>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div class="flex flex-col min-h-full">
          <div class="flex-grow">
            <RouterView />
          </div>
        </div>
      </IonContent>

      <IonFooter>
        <IonToolbar color="primary">
          <IonButtons class="ion-justify-content-center pt-2 pb-3 text-black hover:bg-inherit">
            <IonButton color="black" style="margin: 0 5px">
              <router-link to="/">
                <IonIcon :icon="homeSharp" style="font-size: 28px; fill: black" />
                <br />
                <IonLabel style="font-size: x-small; color: black">Start</IonLabel>
              </router-link>
            </IonButton>
            <IonButton color="black" style="margin: 0 5px">
              <router-link to="/partner">
                <IonIcon :icon="locationSharp" style="font-size: 28px; fill: black" />
                <br />
                <IonLabel style="font-size: x-small; color: black">Partner</IonLabel>
              </router-link>
            </IonButton>
            <IonButton color="black" id="open-geocard-modal" style="margin: 0 5px">
              <div>
                <IonIcon src="/img/icons/geocard-icon.svg" style="font-size: 28px; fill: black" />
                <br />
                <IonLabel style="font-size: x-small; text-transform: none; color: black">GEOcard</IonLabel>
              </div>
            </IonButton>
            <IonButton color="black" style="margin: 0 5px">
              <a
                href="https://shop.geo.de/de_DE/geocard-rabatt.html?onwewe=08105&utm_source=&utm_medium=shop-teaser&utm_campaign=geo-div-geocardrabatt_20%25imshop-non"
                target="_blank"
                rel="noopener"
              >
                <IonIcon :icon="cartSharp" style="font-size: 28px; fill: black" />
                <br />
                <IonLabel style="font-size: x-small; color: black">Shop</IonLabel>
              </a>
            </IonButton>
            <IonButton color="black" style="margin: 0 5px">
              <router-link to="/downloads">
                <IonIcon :icon="imagesSharp" style="font-size: 28px; fill: black" />
                <br />
                <IonLabel style="font-size: x-small; color: black">Downloads</IonLabel>
              </router-link>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>

      <IonActionSheet
        :is-open="accountMenuOpen"
        @didDismiss="accountMenuOpen = false"
        header="Mein Account"
        :buttons="accountMenuButtons"
        mode="ios"
      />

      <IonToast
        :is-open="newVersionAvailable"
        message="Neue App Version wird geladen..."
        position="top"
        position-anchor="header"
      ></IonToast>

      <IonModal trigger="open-geocard-modal" :initial-breakpoint="1" :breakpoints="[0, 1]">
        <div class="geocard-block">
          <div
            v-if="isLoggedIn && currentUser?.subs && currentUser?.subs.length > 0"
            class="relative w-[350px] h-[220px] bg-no-repeat bg-cover text-black rounded-xl overflow-hidden"
            style="background-image: url('/img/geocard.svg')"
          >
            <div class="absolute top-6 right-6 mt-[5.2rem] me-2 text-right">
              <p class="text-xl font-bold">
                {{ new Date().toLocaleDateString("de-DE", { month: "long", year: "numeric" }) }}
              </p>
              <p class="text-xs">Prüf-Code: {{ checkCode }}</p>
            </div>
            <div class="absolute bottom-6 left-6 ml-3 text-left">
              <p class="text-base">{{ currentUser?.name }}</p>
            </div>
          </div>
          <div v-else-if="currentUser?.subs.length == 0" class="relative w-full p-2 text-center">
            Sie haben derzeit kein aktives Abo, um eine persönliche GEOcard anzuzeigen.
          </div>
          <div v-else class="relative w-full p-2 text-center">
            Bitte einloggen, um die persönliche GEOcard anzuzeigen.
          </div>
        </div>
      </IonModal>
    </IonPage>
  </IonApp>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import {
  IonApp,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonButton,
  IonIcon,
  IonList,
  IonItem,
  IonPopover,
  IonLabel,
  IonBadge,
  IonActionSheet,
  IonFooter,
  IonModal,
  IonListHeader,
  IonToast,
} from "@ionic/vue";
import { useAuth } from "@/services/auth";
import { notificationsSharp, personSharp, imagesSharp, homeSharp, cartSharp, locationSharp } from "ionicons/icons";
import { newVersionAvailable } from "@/registerServiceWorker";
import { useRouter } from "vue-router";
import { useCurrentUser } from "@/composables/useCurrentUser";
import { useMessages } from "@/composables/useMessages";

const { isLoggedIn, logout, checkLoginStatus } = useAuth();
const { currentUser, loadUser } = useCurrentUser();
const { messages, unreadMessages, unreadCount, fetchMessages } = useMessages();

const popoverOpen = ref(false);
const router = useRouter();

const REQUIRED_SCHEMA_VERSION = "v2";

function openPopover() {
  popoverOpen.value = true;
}

function closePopover() {
  popoverOpen.value = false;
}

function handleLinkClick(e: MouseEvent) {
  const target = e.target as HTMLElement;
  const a = target.closest("a[href]") as HTMLAnchorElement | null;
  if (!a || !(window as any).umami) return;
  if (a.hasAttribute("data-umami-event")) return;

  const isExternal = a.host !== window.location.host;
  if (!isExternal) return;

  const payload = {
    target: a.href,
    page: window.location.pathname,
  };

  (window as any).umami.track("outbound-link-click", payload);
}

const checkCode = computed(() => {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, "0"); // Monat zweistellig
  const jj = String(now.getFullYear()).slice(-2); // Jahr zweistellig

  const userRaw = localStorage.getItem("gcU");
  if (!userRaw) return `${mm}-${jj}-De-Fa`;

  try {
    const user = JSON.parse(userRaw);
    if (!user.firstname || !user.lastname) return `${mm}-${jj}-De-Fa`;

    const vorname = user.firstname.substring(0, 2);
    const nachname = user.lastname.substring(0, 2);

    return `${mm}-${jj}-${vorname}-${nachname}`;
  } catch (e) {
    return `${mm}-${jj}-De-Fa`;
  }
});

const startLogin = async () => {
  window.location.href = process.env.VUE_APP_API_URL + "/api/login-start";
};

onMounted(() => {
  document.addEventListener("click", handleLinkClick);

  const userRaw = localStorage.getItem("gcU");
  if (userRaw) {
    try {
      const user = JSON.parse(userRaw);

      if (user.schemaVersion !== REQUIRED_SCHEMA_VERSION) {
        localStorage.removeItem("gcU");
        localStorage.removeItem("gcU_exp");
        handleLogout();
        return;
      }
    } catch (err) {
      localStorage.removeItem("gcU");
      localStorage.removeItem("gcU_exp");
      handleLogout();
      return;
    }
  }

  checkLoginStatus();
  if (isLoggedIn.value) {
    loadUser();
    fetchMessages();
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleLinkClick);
});

const handleLogout = () => {
  logout();
};

const accountMenuOpen = ref(false);
const accountMenuButtons = computed(() => {
  if (isLoggedIn.value) {
    const buttons = [];

    if (currentUser.value?.subs?.length > 0) {
      buttons.push({
        text: "Benachrichtigungen",
        handler: () => router.push("/account"),
      });
    }

    buttons.push({
      text: "Abmelden",
      role: "destructive",
      handler: () => handleLogout(),
    });

    return buttons;
  } else {
    return [
      {
        text: "Registrierung",
        handler: () => window.open(process.env.VUE_APP_GUJ_REGISTER_URL, "_blank", "noopener,noreferrer"),
      },
      {
        text: "Login",
        handler: () => startLogin(),
      },
    ];
  }
});

const openAccountMenu = () => {
  accountMenuOpen.value = true;
};

const goToMessage = (id: number) => {
  closePopover();
  router.push({ path: "/account", query: { id } });
};
</script>

<style>
.geocard-block {
  width: 100%;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

ion-modal {
  --height: auto;
}
ion-button {
  --background: var(--ion-color-primary);
  --background-hover: var(--ion-color-primary-shade);
  --color: black;
  height: 44px;
}
</style>
